import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { useState } from 'react';
import WorkspacesList from '@Components/WorkspaceManager/List';
import WorkspaceForm from '@Components/WorkspaceManager/Form';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import type { Workspace } from '@Types/workspace';

const CREATE_FORM_ID = 'create-workspace-form';
const EDIT_FORM_ID = 'edit-workspace-form';

export default function WorkspaceManager() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editWorkspace, setEditWorkspace] = useState<Workspace | null>(null);

  const handleEditClick = (row: { original: Workspace }) => {
    setEditWorkspace(row.original);
    setEditOpen(true);
  };

  const handleCreateClick = () => {
    setCreateOpen(true);
  };

  const gatherDashboardIds = (workspace) => {
    return workspace?.dashboards?.map((dash) => dash.dashboardId) ?? [];
  };

  return (
    <div>
      <Breadcrumbs
        navList={[
          { name: 'Workspaces', href: '/workspaces' },
          { name: 'Manager', href: '/workspaces/manager' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Workspaces</Heading>
        </div>

        {/* Create Sheet */}
        <Sheet open={createOpen} onOpenChange={setCreateOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl">
            <SheetHeader>
              <SheetTitle>Create workspace</SheetTitle>
              <SheetDescription>
                Add a new workspace with a name and description.
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="grow">
              <WorkspaceForm
                onSuccess={() => setCreateOpen(false)}
                submitLabel="Create"
                formId={CREATE_FORM_ID}
                hideInternalActions
              />
            </SheetBody>

            <SheetFooter className="mt-4">
              <Button
                type="submit"
                form={CREATE_FORM_ID}
                variant="primary"
                className="grow"
              >
                Create
              </Button>
              <SheetClose asChild>
                <Button variant="secondary" type="button" className="grow">
                  Cancel
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <WorkspacesList
        onEditClick={handleEditClick}
        onCreateClick={handleCreateClick}
      />

      {/* Edit Sheet */}
      <Sheet
        open={editOpen}
        onOpenChange={(isOpen) => {
          setEditOpen(isOpen);
          if (!isOpen) setEditWorkspace(null);
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Edit workspace</SheetTitle>
            <SheetDescription>
              Update the workspace name, description and resource.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="grow">
            {editWorkspace && (
              <WorkspaceForm
                key={editWorkspace.id}
                editId={editWorkspace.id}
                defaultValues={{
                  name: editWorkspace.name ?? '',
                  description: editWorkspace.description ?? '',
                  color: editWorkspace.color ?? '',
                  icon: editWorkspace.icon ?? '',
                  dashboardIds: gatherDashboardIds(editWorkspace) ?? [],
                }}
                onSuccess={() => {
                  setEditOpen(false);
                  setEditWorkspace(null);
                }}
                submitLabel="Update"
                formId={EDIT_FORM_ID}
                hideInternalActions
              />
            )}
          </SheetBody>

          <SheetFooter className="mt-4">
            <Button
              type="submit"
              form={EDIT_FORM_ID}
              variant="primary"
              className="grow"
            >
              Update
            </Button>
            <SheetClose asChild>
              <Button variant="secondary" type="button" className="grow">
                Cancel
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
